import { MarketingCsvImportPanel } from '../imports/MarketingCsvImportPanel';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  FlaskConical,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  Trash2,
  UserRoundCheck,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { EmptyState } from '../../../components/EmptyState';
import { Alert } from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import {
  marketingApi,
  type MarketingAudienceEligibilityReason,
  type MarketingSegmentDefinition,
  type MarketingSegmentDetail,
  type MarketingSegmentRule,
} from '../../../lib/api';
import {
  createDefaultMarketingSegmentDefinition,
  createMarketingSegmentRule,
  dateTimeLocalToIso,
  dateTimeLocalValue,
  type MarketingSegmentRuleField,
} from './marketing-segment-builder';

const RULE_FIELD_LABELS: Record<MarketingSegmentRuleField, string> = {
  EMAIL: 'Adres email',
  EMAIL_CONSENT: 'Zgoda marketingowa',
  EMAIL_PREFERENCE: 'Preferencja email',
  ACQUISITION_TYPE: 'Źródło pozyskania',
  CONTACT_DATE: 'Data kontaktu',
  PURCHASE_HISTORY: 'Historia zakupów',
};

const ACQUISITION_LABELS = {
  ORDER: 'Zamówienie',
  FORM: 'Formularz',
  QUIZ: 'Quiz',
  CSV_IMPORT: 'Import CSV',
  API: 'API',
  MANUAL: 'Ręcznie',
} as const;

const REASON_LABELS: Record<MarketingAudienceEligibilityReason, string> = {
  EMAIL_IDENTITY_MISSING: 'Brak adresu email',
  CONTACT_NOT_ACTIVE: 'Kontakt nieaktywny',
  CONSENT_MISSING: 'Brak zgody',
  CONSENT_WITHDRAWN: 'Zgoda cofnięta',
  PREFERENCE_DISABLED: 'Email wyłączony w preferencjach',
  SCOPED_SUPPRESSION: 'Aktywne wyciszenie marketingowe',
  LEGACY_SUPPRESSION: 'Blokada w istniejącej suppression list',
};

let ruleDraftSequence = 0;

type RuleDraft = { id: string; rule: MarketingSegmentRule };

function createRuleDraft(rule: MarketingSegmentRule): RuleDraft {
  ruleDraftSequence += 1;
  return { id: `segment-rule-${ruleDraftSequence}`, rule };
}

function apiErrorMessage(error: unknown): string {
  if (isAxiosError<{ error?: string }>(error)) {
    return error.response?.data?.error || 'Nie udało się wykonać operacji. Spróbuj ponownie.';
  }
  return 'Nie udało się wykonać operacji. Spróbuj ponownie.';
}

function RuleEditor({
  draft,
  index,
  canRemove,
  onChange,
  onRemove,
}: {
  draft: RuleDraft;
  index: number;
  canRemove: boolean;
  onChange: (rule: MarketingSegmentRule) => void;
  onRemove: () => void;
}) {
  const rule = draft.rule;
  const fieldId = `segment-rule-field-${draft.id}`;

  return (
    <div className="grid gap-3 rounded-xl border border-border-subtle bg-surface-base/30 p-3 lg:grid-cols-[minmax(180px,0.9fr)_minmax(280px,1.6fr)_40px] lg:items-end">
      <div>
        <label htmlFor={fieldId} className="mb-1.5 block text-xs font-medium text-text-secondary">
          Warunek {index + 1}
        </label>
        <Select
          id={fieldId}
          value={rule.field}
          onChange={event => onChange(createMarketingSegmentRule(event.target.value as MarketingSegmentRuleField))}
        >
          {Object.entries(RULE_FIELD_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </Select>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {rule.field === 'EMAIL' && (
          <Select
            value={rule.operator}
            onChange={event => onChange({ ...rule, operator: event.target.value as 'IS_SET' | 'IS_NOT_SET' })}
            aria-label={`Operator warunku ${index + 1}`}
          >
            <option value="IS_SET">jest dostępny</option>
            <option value="IS_NOT_SET">nie jest dostępny</option>
          </Select>
        )}

        {rule.field === 'EMAIL_CONSENT' && (
          <Select
            value={rule.value}
            aria-label={`Wartość zgody w warunku ${index + 1}`}
            onChange={event => onChange({ ...rule, value: event.target.value as 'GRANTED' | 'WITHDRAWN' | 'MISSING' })}
          >
            <option value="GRANTED">udzielona</option>
            <option value="WITHDRAWN">cofnięta</option>
            <option value="MISSING">brak zapisu</option>
          </Select>
        )}

        {rule.field === 'EMAIL_PREFERENCE' && (
          <Select
            value={rule.value}
            aria-label={`Wartość preferencji w warunku ${index + 1}`}
            onChange={event => onChange({ ...rule, value: event.target.value as 'ENABLED' | 'DISABLED' | 'DEFAULT' })}
          >
            <option value="ENABLED">jawnie włączona</option>
            <option value="DISABLED">wyłączona</option>
            <option value="DEFAULT">brak własnego ustawienia</option>
          </Select>
        )}

        {rule.field === 'ACQUISITION_TYPE' && (
          <>
            <Select
              value={rule.operator}
              aria-label={`Operator źródła w warunku ${index + 1}`}
              onChange={event => onChange({ ...rule, operator: event.target.value as 'IS' | 'IS_NOT' })}
            >
              <option value="IS">obejmuje</option>
              <option value="IS_NOT">nie obejmuje</option>
            </Select>
            <Select
              value={rule.value}
              aria-label={`Źródło pozyskania w warunku ${index + 1}`}
              onChange={event => onChange({ ...rule, value: event.target.value as typeof rule.value })}
            >
              {Object.entries(ACQUISITION_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
          </>
        )}

        {rule.field === 'CONTACT_DATE' && (
          <>
            <Select
              value={rule.dateField}
              aria-label={`Pole daty w warunku ${index + 1}`}
              onChange={event => onChange({ ...rule, dateField: event.target.value as 'FIRST_SEEN_AT' | 'LAST_SEEN_AT' })}
            >
              <option value="FIRST_SEEN_AT">Pierwszy kontakt</option>
              <option value="LAST_SEEN_AT">Ostatnia aktywność</option>
            </Select>
            <Select
              value={rule.operator}
              aria-label={`Operator daty w warunku ${index + 1}`}
              onChange={event => onChange({ ...rule, operator: event.target.value as 'BEFORE' | 'AFTER' })}
            >
              <option value="AFTER">od daty</option>
              <option value="BEFORE">przed datą</option>
            </Select>
            <Input
              type="datetime-local"
              value={dateTimeLocalValue(rule.value)}
              aria-label={`Data warunku ${index + 1}`}
              className="sm:col-span-2"
              onChange={event => {
                const value = dateTimeLocalToIso(event.target.value);
                if (value) onChange({ ...rule, value });
              }}
            />
          </>
        )}

        {rule.field === 'PURCHASE_HISTORY' && (
          <Select
            value={rule.operator}
            aria-label={`Historia zakupów w warunku ${index + 1}`}
            onChange={event => onChange({ ...rule, operator: event.target.value as 'HAS_PAID_ORDER' | 'NO_PAID_ORDER' })}
          >
            <option value="HAS_PAID_ORDER">ma opłacone zamówienie</option>
            <option value="NO_PAID_ORDER">nie ma opłaconego zamówienia</option>
          </Select>
        )}
      </div>

      <Button
        variant="icon"
        size="md"
        onClick={onRemove}
        disabled={!canRemove}
        aria-label={`Usuń warunek ${index + 1}`}
        leftIcon={<Trash2 />}
      />
    </div>
  );
}

function SegmentPreview({ preview }: {
  preview: Awaited<ReturnType<typeof marketingApi.previewSegment>>['data'];
}) {
  const stats = [
    { label: 'Aktywne kontakty', value: preview.totalContacts },
    { label: 'Pasuje do segmentu', value: preview.matchedContacts },
    { label: 'Z adresem email', value: preview.contactsWithEmail },
    { label: 'Z aktualną zgodą', value: preview.contactsWithConsent },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map(stat => (
          <div key={stat.label} className="rounded-xl border border-border-subtle bg-surface-base/30 p-3">
            <p className="text-xs text-text-muted">{stat.label}</p>
            <p className="mt-1 text-2xl font-bold text-text-primary">{stat.value.toLocaleString('pl-PL')}</p>
          </div>
        ))}
      </div>

      {preview.items.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Brak kontaktów spełniających reguły"
          description="Zmień warunki segmentu i odśwież podgląd danych."
          size="sm"
        />
      ) : (
        <div className="table-container overflow-x-auto">
          <table className="table min-w-[900px]">
            <thead>
              <tr>
                <th>Odbiorca</th>
                <th>Zgoda</th>
                <th>Źródła</th>
                <th>Zakupy</th>
                <th>Eligibility</th>
                <th>Ostatnia aktywność</th>
              </tr>
            </thead>
            <tbody>
              {preview.items.map(contact => {
                const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
                return (
                  <tr key={contact.id}>
                    <td>
                      <p className="font-medium text-text-primary">{name || 'Bez nazwy'}</p>
                      <p className="mt-0.5 text-xs text-text-muted">{contact.primaryEmail || 'Brak adresu email'}</p>
                    </td>
                    <td>
                      <Badge
                        size="sm"
                        variant={contact.consentStatus === 'GRANTED' ? 'success' : contact.consentStatus === 'WITHDRAWN' ? 'danger' : 'warning'}
                      >
                        {contact.consentStatus === 'GRANTED' ? 'Udzielona' : contact.consentStatus === 'WITHDRAWN' ? 'Cofnięta' : 'Brak'}
                      </Badge>
                    </td>
                    <td className="text-xs text-text-secondary">
                      {contact.acquisitionTypes.length > 0
                        ? contact.acquisitionTypes.map(type => ACQUISITION_LABELS[type]).join(', ')
                        : 'Brak'}
                    </td>
                    <td className="text-sm text-text-secondary">{contact.paidOrderCount}</td>
                    <td>
                      {contact.eligible ? (
                        <Badge size="sm" variant="success">Możliwy</Badge>
                      ) : (
                        <div className="flex max-w-64 flex-wrap gap-1">
                          {contact.reasonCodes.map(reason => (
                            <Badge key={reason} size="sm" variant="warning">{REASON_LABELS[reason]}</Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="text-xs text-text-muted">
                      {new Date(contact.lastSeenAt).toLocaleString('pl-PL')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-text-muted">
        Podgląd pokazuje {preview.items.length} z {preview.matchedContacts.toLocaleString('pl-PL')} kontaktów i nie zapisuje snapshotu odbiorców.
      </p>
    </div>
  );
}

function SegmentEditor({
  marketingBrandId,
  segment,
  onSaved,
}: {
  marketingBrandId: string;
  segment?: MarketingSegmentDetail;
  onSaved: (segment: MarketingSegmentDetail) => void;
}) {
  const queryClient = useQueryClient();
  const initialDefinition = segment?.versions[0]?.definition ?? createDefaultMarketingSegmentDefinition();
  const [name, setName] = useState(segment?.name ?? 'Nowy segment');
  const [description, setDescription] = useState(segment?.description ?? '');
  const [operator, setOperator] = useState(initialDefinition.operator);
  const [ruleDrafts, setRuleDrafts] = useState(() => initialDefinition.rules.map(createRuleDraft));
  const definition = useMemo<MarketingSegmentDefinition>(() => ({
    schemaVersion: 1,
    operator,
    rules: ruleDrafts.map(draft => draft.rule),
  }), [operator, ruleDrafts]);

  const previewMutation = useMutation({
    mutationFn: () => marketingApi.previewSegment(marketingBrandId, definition),
  });
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (segment) {
        return marketingApi.createSegmentDraftVersion(marketingBrandId, segment.id, {
          definition,
          expectedLatestVersionNumber: segment.latestVersionNumber,
        });
      }
      return marketingApi.createSegment(marketingBrandId, { name, description, definition });
    },
    onSuccess: async response => {
      queryClient.setQueryData(
        ['marketing-segment', marketingBrandId, response.data.id],
        response.data,
      );
      await queryClient.invalidateQueries({ queryKey: ['marketing-segments', marketingBrandId] });
      onSaved(response.data);
    },
  });

  const updateRule = (id: string, rule: MarketingSegmentRule) => {
    previewMutation.reset();
    setRuleDrafts(current => current.map(draft => draft.id === id ? { ...draft, rule } : draft));
  };

  return (
    <div className="space-y-5">
      {segment ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">{segment.name}</h3>
            <p className="mt-1 text-xs text-text-muted">
              Immutable v{segment.latestVersionNumber} - zapis tworzy nową wersję reguł.
            </p>
          </div>
          <Badge variant="accent">v{segment.latestVersionNumber}</Badge>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label htmlFor="new-segment-name" className="mb-1.5 block text-xs font-medium text-text-secondary">Nazwa segmentu</label>
            <Input id="new-segment-name" value={name} maxLength={120} onChange={event => setName(event.target.value)} />
          </div>
          <div>
            <label htmlFor="new-segment-description" className="mb-1.5 block text-xs font-medium text-text-secondary">Opis opcjonalny</label>
            <Input id="new-segment-description" value={description} maxLength={2_000} onChange={event => setDescription(event.target.value)} />
          </div>
        </div>
      )}

      <div className="w-full sm:w-72">
        <label htmlFor="segment-rule-operator" className="mb-1.5 block text-xs font-medium text-text-secondary">Łączenie warunków</label>
        <Select
          id="segment-rule-operator"
          value={operator}
          onChange={event => {
            previewMutation.reset();
            setOperator(event.target.value as 'ALL' | 'ANY');
          }}
        >
          <option value="ALL">Wszystkie warunki</option>
          <option value="ANY">Dowolny warunek</option>
        </Select>
      </div>

      <div className="space-y-3">
        {ruleDrafts.map((draft, index) => (
          <RuleEditor
            key={draft.id}
            draft={draft}
            index={index}
            canRemove={ruleDrafts.length > 1}
            onChange={rule => updateRule(draft.id, rule)}
            onRemove={() => {
              previewMutation.reset();
              setRuleDrafts(current => current.filter(item => item.id !== draft.id));
            }}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          leftIcon={<Save />}
          loading={saveMutation.isPending}
          disabled={!name.trim() || ruleDrafts.length === 0}
          onClick={() => saveMutation.mutate()}
        >
          {segment ? 'Zapisz nową wersję' : 'Zapisz segment'}
        </Button>
        <Button
          variant="secondary"
          leftIcon={<Eye />}
          loading={previewMutation.isPending}
          onClick={() => previewMutation.mutate()}
        >
          Odśwież podgląd danych
        </Button>
        <Button
          variant="ghost"
          leftIcon={<Plus />}
          disabled={ruleDrafts.length >= 20}
          onClick={() => {
            previewMutation.reset();
            setRuleDrafts(current => [...current, createRuleDraft(createMarketingSegmentRule('EMAIL_CONSENT'))]);
          }}
        >
          Dodaj warunek
        </Button>
      </div>

      {saveMutation.isError && <Alert variant="danger">{apiErrorMessage(saveMutation.error)}</Alert>}
      {previewMutation.isError && <Alert variant="danger">{apiErrorMessage(previewMutation.error)}</Alert>}
      {previewMutation.data && <SegmentPreview preview={previewMutation.data.data} />}
    </div>
  );
}

function SegmentWorkspace({ marketingBrandId }: { marketingBrandId: string }) {
  const [selectedSegmentId, setSelectedSegmentId] = useState('');
  const segmentsQuery = useQuery({
    queryKey: ['marketing-segments', marketingBrandId],
    queryFn: async () => (await marketingApi.listSegments(marketingBrandId)).data,
  });
  const segmentQuery = useQuery({
    queryKey: ['marketing-segment', marketingBrandId, selectedSegmentId],
    queryFn: async () => (await marketingApi.getSegment(marketingBrandId, selectedSegmentId)).data,
    enabled: Boolean(selectedSegmentId),
  });

  const segments = segmentsQuery.data ?? [];
  const selectedExists = segments.some(segment => segment.id === selectedSegmentId);
  const effectiveSegmentId = selectedExists ? selectedSegmentId : '';

  return (
    <Card
      padding="md"
      header={(
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-accent-primary" aria-hidden="true" />
              <h2 className="text-lg font-semibold text-text-primary">Segment builder</h2>
            </div>
            <p className="mt-1 text-xs text-text-muted">Reguły są wersjonowane, a podgląd wykonuje wyłącznie odczyt aktualnych danych.</p>
          </div>
          <div className="w-full sm:w-72">
            <label htmlFor="marketing-segment-select" className="mb-1.5 block text-xs font-medium text-text-secondary">Edytowany segment</label>
            <Select
              id="marketing-segment-select"
              value={effectiveSegmentId}
              onChange={event => setSelectedSegmentId(event.target.value)}
            >
              <option value="">Nowy segment</option>
              {segments.map(item => <option key={item.id} value={item.id}>{item.name} - v{item.latestVersionNumber}</option>)}
            </Select>
          </div>
        </div>
      )}
    >
      {segmentsQuery.isLoading || (effectiveSegmentId && segmentQuery.isLoading) ? (
        <div className="flex min-h-48 items-center justify-center text-accent-primary" aria-label="Ładowanie segmentów">
          <RefreshCw className="h-6 w-6 animate-spin" />
        </div>
      ) : segmentsQuery.isError || segmentQuery.isError ? (
        <EmptyState
          icon={AlertTriangle}
          title="Nie udało się pobrać segmentów"
          description={apiErrorMessage(segmentsQuery.error ?? segmentQuery.error)}
          buttonLabel="Spróbuj ponownie"
          onButtonClick={() => void (segmentsQuery.isError ? segmentsQuery.refetch() : segmentQuery.refetch())}
          size="sm"
        />
      ) : (
        <SegmentEditor
          key={`${marketingBrandId}:${effectiveSegmentId || 'new'}:${segmentQuery.data?.latestVersionNumber ?? 0}`}
          marketingBrandId={marketingBrandId}
          segment={effectiveSegmentId ? segmentQuery.data : undefined}
          onSaved={saved => {
            setSelectedSegmentId(saved.id);
          }}
        />
      )}
    </Card>
  );
}

function TestRecipientsPanel({ marketingBrandId }: { marketingBrandId: string }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');
  const [ownershipConfirmed, setOwnershipConfirmed] = useState(false);
  const recipientsQuery = useQuery({
    queryKey: ['marketing-test-recipients', marketingBrandId],
    queryFn: async () => (await marketingApi.listTestRecipients(marketingBrandId)).data,
  });
  const addMutation = useMutation({
    mutationFn: () => marketingApi.addTestRecipient(marketingBrandId, {
      email,
      label,
      ownershipConfirmed: true,
    }),
    onSuccess: async () => {
      setEmail('');
      setLabel('');
      setOwnershipConfirmed(false);
      await queryClient.invalidateQueries({ queryKey: ['marketing-test-recipients', marketingBrandId] });
    },
  });
  const statusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => (
      marketingApi.setTestRecipientActive(marketingBrandId, id, isActive)
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['marketing-test-recipients', marketingBrandId] });
    },
  });

  const recipients = recipientsQuery.data ?? [];
  return (
    <Card
      padding="md"
      header={(
        <div className="flex items-center gap-2">
          <FlaskConical className="h-5 w-5 text-accent-primary" aria-hidden="true" />
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Odbiorcy testowi</h2>
            <p className="mt-1 text-xs text-text-muted">Jawna allowlista skrzynek kontrolowanych przez operatora.</p>
          </div>
        </div>
      )}
    >
      <div className="space-y-4">
        <Alert variant="info" title="Wysyłka pozostaje wyłączona">
          Ten widok tylko zapisuje allowlistę. Nie uruchamia providera, wysyłki testowej ani webhooka.
        </Alert>

        <div className="grid gap-3 lg:grid-cols-2">
          <div>
            <label htmlFor="test-recipient-email" className="mb-1.5 block text-xs font-medium text-text-secondary">Adres email</label>
            <Input id="test-recipient-email" type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="test@firma.pl" />
          </div>
          <div>
            <label htmlFor="test-recipient-label" className="mb-1.5 block text-xs font-medium text-text-secondary">Etykieta opcjonalna</label>
            <Input id="test-recipient-label" value={label} maxLength={120} onChange={event => setLabel(event.target.value)} placeholder="Skrzynka QA" />
          </div>
        </div>

        <Checkbox
          checked={ownershipConfirmed}
          onChange={event => setOwnershipConfirmed(event.target.checked)}
          label="Potwierdzam, że kontroluję tę skrzynkę"
          description="Odbiorca testowy nie jest kontaktem marketingowym i nie omija suppression dla klientów."
        />

        <Button
          variant="primary"
          leftIcon={<UserRoundCheck />}
          loading={addMutation.isPending}
          disabled={!email.trim() || !ownershipConfirmed}
          onClick={() => addMutation.mutate()}
        >
          Dodaj do allowlisty
        </Button>

        {(addMutation.isError || statusMutation.isError) && (
          <Alert variant="danger">{apiErrorMessage(addMutation.error ?? statusMutation.error)}</Alert>
        )}

        {recipientsQuery.isLoading ? (
          <div className="flex min-h-32 items-center justify-center text-accent-primary" aria-label="Ładowanie odbiorców testowych">
            <RefreshCw className="h-5 w-5 animate-spin" />
          </div>
        ) : recipientsQuery.isError ? (
          <EmptyState
            icon={AlertTriangle}
            title="Nie udało się pobrać allowlisty"
            description={apiErrorMessage(recipientsQuery.error)}
            buttonLabel="Spróbuj ponownie"
            onButtonClick={() => void recipientsQuery.refetch()}
            size="sm"
          />
        ) : recipients.length === 0 ? (
          <EmptyState
            icon={FlaskConical}
            title="Brak odbiorców testowych"
            description="Dodaj kontrolowaną skrzynkę. Samo dodanie nie uruchomi wysyłki."
            size="sm"
          />
        ) : (
          <div className="space-y-2">
            {recipients.map(recipient => (
              <div key={recipient.id} className="flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-base/30 p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-medium text-text-primary">{recipient.normalizedEmail}</p>
                    <Badge size="sm" variant={recipient.isActive ? 'success' : 'neutral'}>
                      {recipient.isActive ? 'Aktywny' : 'Wyłączony'}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-text-muted">
                    {recipient.label || 'Bez etykiety'} - kontrola potwierdzona {new Date(recipient.ownershipConfirmedAt).toLocaleDateString('pl-PL')}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={statusMutation.isPending && statusMutation.variables?.id === recipient.id}
                  leftIcon={recipient.isActive ? <ShieldCheck /> : <CheckCircle2 />}
                  onClick={() => statusMutation.mutate({ id: recipient.id, isActive: !recipient.isActive })}
                >
                  {recipient.isActive ? 'Wyłącz' : 'Włącz'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export function MarketingAudiencePanel() {
  const [brandId, setBrandId] = useState('');
  const brandsQuery = useQuery({
    queryKey: ['marketing-brands'],
    queryFn: async () => (await marketingApi.listBrands()).data,
    staleTime: 60_000,
  });
  const brands = brandsQuery.data ?? [];
  const selectedBrandId = brands.some(brand => brand.id === brandId) ? brandId : brands[0]?.id ?? '';

  if (brandsQuery.isLoading) {
    return <div className="flex min-h-48 items-center justify-center text-accent-primary" aria-label="Ładowanie odbiorców"><RefreshCw className="h-7 w-7 animate-spin" /></div>;
  }
  if (brandsQuery.isError) {
    return <EmptyState icon={AlertTriangle} title="Nie udało się pobrać przestrzeni marki" description={apiErrorMessage(brandsQuery.error)} buttonLabel="Spróbuj ponownie" onButtonClick={() => void brandsQuery.refetch()} />;
  }
  if (!selectedBrandId) {
    return <EmptyState icon={Users} title="Brak przestrzeni marki" description="Utwórz sklep lub MarketingBrand, aby zarządzać odbiorcami email." />;
  }

  return (
    <section className="space-y-6" aria-label="Odbiorcy email">
      <div className="rounded-card border border-border-subtle bg-surface-raised/50 p-3">
        <div className="w-full sm:w-72">
          <label htmlFor="marketing-audience-brand" className="mb-1.5 block text-xs font-medium text-text-secondary">Przestrzeń marki</label>
          <Select id="marketing-audience-brand" value={selectedBrandId} onChange={event => setBrandId(event.target.value)}>
            {brands.map(brand => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
          </Select>
        </div>
      </div>

      <MarketingCsvImportPanel key={`csv:${selectedBrandId}`} marketingBrandId={selectedBrandId} />
      <SegmentWorkspace key={`segments:${selectedBrandId}`} marketingBrandId={selectedBrandId} />
      <TestRecipientsPanel key={`test-recipients:${selectedBrandId}`} marketingBrandId={selectedBrandId} />
    </section>
  );
}
